'use client';

import { createContext, useContext } from 'react';
import { usePathname } from 'next/navigation';
import { isAdAllowedRoute } from './adRoutes';
import { isAdFreeRole } from '../../lib/auth/roles';

const AdContext = createContext(null);

export function AdProvider({ children, user, hasPaidServer, hydrating = false }) {
  const pathname = usePathname();

  // Determine if user should see ads.
  //
  // Rules:
  //   1. Paid-plan users are ad-free by default and can opt in via
  //      `show_ads_on_paid` from the account page.
  //   2. Free-plan users (and logged-out visitors) see ads unless `ads_disabled`
  //      is explicitly set for the account.
  //   3. Admin and above never see ads.
  //
  // `hasPaidServer` is also mirrored on the user object as `has_paid_server`
  // (set by /api/auth/me) so this works even when the prop isn't threaded
  // through to a particular layout.
  const isPaid = !!(hasPaidServer || user?.has_paid_server);
  const hasUser = !!user?.id;
  const hasAdFlags = typeof user?.ads_disabled !== 'undefined' && typeof user?.show_ads_on_paid !== 'undefined';
  // User-level eligibility.
  // Fail closed for authenticated users when ad flags are not hydrated yet.
  const isAdFree = isAdFreeRole(user?.access_role ?? user?.role);
  const userAllowsAds = isAdFree || (hasUser && !hasAdFlags)
    ? false
    : isPaid
      ? !!user?.show_ads_on_paid
      : !user?.ads_disabled;
  // Route-level gate: banners + popup only on /panel and /blog.
  const routeAllowsAds = isAdAllowedRoute(pathname);
  // Gate on `hydrating` here too (not just inside BannerAd) so every
  // consumer of shouldShowAds - the side-rail grid columns, the bottom ad
  // bar, PanelPageAd's reserved-space wrapper, etc. - agrees ads are off
  // until we actually know the user's plan/ad-preference. Without this,
  // those consumers briefly saw shouldShowAds=true (userAllowsAds defaults
  // true before hydration resolves) and rendered their ad skeleton/frame
  // for a flash before flipping back off once hydration finished - most
  // visibly for paid users with ads turned off, who should never see it.
  const shouldShowAds = !hydrating && userAllowsAds && routeAllowsAds;

  return (
    <AdContext.Provider value={{
      shouldShowAds,
      userAllowsAds,
      routeAllowsAds,
      hydrating,
      user,
    }}>
      {children}
    </AdContext.Provider>
  );
}

export function useAds() {
  const context = useContext(AdContext);
  if (!context) {
    return {
      shouldShowAds: false,
      userAllowsAds: false,
      routeAllowsAds: false,
      hydrating: true,
      user: null,
    };
  }
  return context;
}

